import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, OnChanges, signal, inject, ChangeDetectionStrategy, InputSignal, input, effect, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { BreakpointsService } from 'src/app/services';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { HtmlDecodePipe } from 'src/app/pipes/html-decode.pipe';
import { ScrollService } from 'src/chatbot/service/scrollContainer.service';
import { EmbedContextService } from 'src/app/services/embed-context.service';
import { ShowItemDetailsService } from 'src/chatbot/service/chat-actions/show-item-details.service';
import { ToolsService } from 'src/app/services/tools.service';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';

interface LinkPreviewData {
    title?: string;
    description?: string;
    image?: string;
    url: string;
    site_name?: string;
}

// Cache estático para evitar llamadas duplicadas
const previewCache = new Map<string, LinkPreviewData | null>();
const pendingRequests = new Map<string, Promise<LinkPreviewData | null>>();

@Component({
    selector: 'link-preview',
    standalone: true,
    imports: [
        CommonModule, 
        HtmlDecodePipe
    ],
    templateUrl: './link-preview.component.html',
    styleUrls: ['./link-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkPreviewComponent {
    private router = inject(Router);
    private http = inject(HttpClient);
    private dataChatService = inject(DataChatService);
    private breakpointsService = inject(BreakpointsService);
    private scrollService = inject(ScrollService);
    private embedContext = inject(EmbedContextService);
    private showItemDetails = inject(ShowItemDetailsService);
    private toolsService = inject(ToolsService);
    private customRouteService = inject(CustomRouteService);
    private location = inject(Location);
    
    @Input({ required: true }) url!: string;
    @Input() colorVariant: number = 0; // 0-3 para variaciones sutiles de azul

    private isMobile = computed(() => this.breakpointsService.isMobile());
    preview = signal<LinkPreviewData | null>(null);
    loading = signal(true);
    error = signal(false);
    imageError = signal(false);
    // HRS-3045: true si llegó a pintarse el skeleton (hubo espera de red). El estado
    // fallback solo reserva la altura del skeleton cuando lo reemplaza, para no
    // generar CLS; el fallback directo (URL corta) sigue compacto.
    hadSkeleton = signal(false);

    ngOnInit() {
        // HRS-3045: sin retardo artificial. El template del bubble solo instancia
        // <link-preview> cuando message.finished === true, así que la URL ya es
        // definitiva al llegar aquí; el antiguo setTimeout(750) era espera muerta.
        this.loadPreviewIfNeeded();
    }

    private loadPreviewIfNeeded() {
        if (!this.url || this.url.length < 10) {
            // URL inválida/corta: tarjeta fallback (el skeleton se quedaba animando para siempre).
            this.error.set(true);
            this.loading.set(false);
            this.scrollAfterRender();
            return;
        }

        // Seed sembrado por una acción SHOW_POI/POIS: la acción ya trae título e imagen,
        // así que se pinta directamente SIN petición a /api/link-preview (HRS-3045: evita
        // el N+1 y la tarjeta aparece a coste cero).
        // const seed = this.dataChatService.linkPreviewMeta?.get(this.url);
        // if (seed?.title) {
        //     this.preview.set({ url: this.url, ...seed });
        //     this.loading.set(false);
        //     this.scrollAfterRender();
        //     return;
        // }

        // Si ya está en cache, usar directamente
        if (previewCache.has(this.url)) {
            const cached = previewCache.get(this.url);
            if (cached) {
                this.preview.set(cached);
            } else {
                this.error.set(true);
            }
            this.loading.set(false);
            this.scrollAfterRender();
            return;
        }

        // Si ya hay una petición pendiente para esta URL, esperar
        if (pendingRequests.has(this.url)) {
            this.hadSkeleton.set(true);
            pendingRequests.get(this.url)!.then(data => {
                if (data) {
                    this.preview.set(data);
                } else {
                    this.error.set(true);
                }
                this.loading.set(false);
                this.scrollAfterRender();
            });
            return;
        }

        this.fetchPreview();
    }

    private async fetchPreview() {
        const url = this.url;
        this.hadSkeleton.set(true);

        // Seed opcional sembrado por una acción del asistente (SHOW_POI/POIS): solo se usa
        // como FALLBACK si el backend no resuelve ficha (p.ej. self-hosted que solo scrapea).
        const seed = this.dataChatService.linkPreviewMeta?.get(url);
        const seedData: LinkPreviewData | null = seed ? { url, ...seed } : null;

        const requestPromise = (async (): Promise<LinkPreviewData | null> => {
            try {
                const apiUrl = `${environment.apiAssistantUrlBase}/api/link-preview?url=${encodeURIComponent(url)}`;
                const data = await this.http.get<LinkPreviewData>(apiUrl).toPromise();

                // El backend resolvió ficha rica (imagen o descripción) → usarla (mejor que el seed).
                if (data && (data.image || data.description)) {
                    // Convertir URLs de imagen relativas a absolutas (para imágenes generadas por Gemini)
                    if (data.image && data.image.startsWith('/api/')) {
                        data.image = `${environment.apiAssistantUrlBase}${data.image}`;
                    }
                    return data;
                }
                // Backend genérico/vacío (self-hosted scrapea) → usar el seed de la acción si lo hay.
                if (seedData) return seedData;
                return (data && data.title) ? data : null;
            } catch (e) {
                console.warn('[LinkPreview] Error fetching preview:', e);
                return seedData;
            }
        })();

        pendingRequests.set(url, requestPromise);

        try {
            const result = await requestPromise;
            previewCache.set(url, result);

            if (result) {
                this.preview.set(result);
            } else {
                this.error.set(true);
            }
        } finally {
            pendingRequests.delete(url);
            this.loading.set(false);
            this.scrollAfterRender();
        }
    }

    private scrollAfterRender() {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.scrollService.scrollToBottom();
            });
        });
    }

    onImageLoad() {
        this.scrollAfterRender();
    }

    onImageError() {
        this.imageError.set(true);
        this.scrollAfterRender();
    }

    openLink() {
        if (this.url.includes('/event/') || this.url.includes('/point/') || this.url.includes('/hiking/')){
            const resource = this.parseResource(this.url);
            // ¿Estamos en el planner, en CUALQUIER superficie? site /custom-route, modo
            // planner (?planner=true), o el embed (script) con la ruta montada
            // (routeReady = CustomRouteService.token(); el embed monta <app-custom-route>
            // sin cambiar la URL). En todos: abrir el detalle como dialog (desktop) /
            // bottom-sheet (mobile), sin navegar ni abrir pestaña.
            const inPlanner = this.router.url.includes('custom-route')
                || this.toolsService.isPlannerMode()
                || (this.embedContext.isEmbed() && !!this.customRouteService.token());
            if (inPlanner && resource && (resource.type === 'point' || resource.type === 'event')) {
                this.showItemDetails.show({ element_id: resource.id, type: resource.type }).subscribe();
                return;
            }

            // Embed (widget de chat) SIN planner: pestaña nueva, no machacar el chat. [HRS-2434]
            if (this.embedContext.isEmbed()) {
                window.open(this.url, '_blank', 'noopener,noreferrer');
                return;
            }

            // Site sin planner: navegar a la ficha como antes. El pathname puede traer el
            // base href del despliegue (p.ej. /front/ en visit.madrid); navigateByUrl espera
            // la ruta SIN ese prefijo, así que se normaliza con Location.
            const target = new URL(this.url);
            const path = this.location.normalize(target.pathname) + target.search + target.hash;
            this.router.navigateByUrl(path);
            if (this.isMobile()){
                this.dataChatService.hideChat.set(true);
            }
            return;
        }

        // Enlace externo (no recurso interno): pestaña nueva.
        window.open(this.url, '_blank', 'noopener,noreferrer');
    }

    /** Extrae {type,id} de una URL de recurso /point|event|hiking/{slug}/{id}. */
    private parseResource(url: string): { id: string; type: 'point' | 'event' | 'hiking' } | null {
        const m = url.match(/\/(point|event|hiking)\/[^/]+\/(\d+)/);
        return m ? { type: m[1] as 'point' | 'event' | 'hiking', id: m[2] } : null;
    }

    /** Devuelve la URL de forma legible (solo dominio) */
    getDisplayUrl(): string {
        try {
            const parsed = new URL(this.url);
            return parsed.hostname.replace('www.', '');
        } catch {
            return this.url;
        }
    }
}
